import { logSearch } from '@/features/GlobalSearch/api/logging';
import { trackSearchEngagement } from '@/utils/analytics';

import { mapAssetTypeToSearchTypeParam } from '@/features/GlobalSearch/utils/mapAssetTypeToSearchTypeParam';
import { buildSearchUrl } from '@/features/SearchPage/utils/searchUrl';

import type { FormValues } from './globalSearchForm';
import type { SearchResults } from './searchStorage';
import type { AssetType } from '@/features/GlobalSearch/types/index';
import type { EventAccess, TrackingContentType } from '@/utils/tracking/types';
import type { NextRouter } from 'next/router';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import type { UseFormReturn } from 'react-hook-form';

interface SearchRef {
  assetType: AssetType;
  keyword: string;
  requestId: number;
}

interface DebouncedSearch {
  cancel: () => void;
  flush: () => void;
  immediate: (keyword: string, assetType: AssetType) => void;
  search: (keyword: string, assetType: AssetType) => void;
}

// Define a proper interface for tracking params to match InitializeTrackingParams
interface TrackingParams {
  access: EventAccess;
  assetType?: AssetType | 'general';
  content: string | undefined;
  contentType?: TrackingContentType;
  endSlug?: string;
  // Allow other properties without using 'any'
  [key: string]:
    | string
    | AssetType
    | 'general'
    | TrackingContentType
    | undefined;
}

export function createSearchHandlers(
  methods: UseFormReturn<FormValues>,
  router: NextRouter,
  setSearchResults: Dispatch<SetStateAction<SearchResults | undefined>>,
  debouncedSearch: DebouncedSearch,
  cachedSearchResults: Record<string, SearchResults>,
  isSearchVisible: boolean,
  openSearch: () => void,
  closeSearch: () => void,
  currentSearchRef: MutableRefObject<SearchRef>,
  requestIdCounter: MutableRefObject<number>,
  currentAssetType: AssetType,
  initializeTracking: (params: TrackingParams) => void | Promise<void> // Allow Promise return
) {
  const getCacheKey = (keyword: string, assetType: AssetType) =>
    `${keyword}::${assetType}`;

  // Enhanced submitSearch function with efficiency improvements
  const submitSearch = (keyword: string, assetType: AssetType) => {
    keyword = keyword.trim();

    if (!keyword) {
      return;
    }

    const searchUrl = buildSearchUrl(
      mapAssetTypeToSearchTypeParam(assetType),
      keyword
    );

    closeSearch();
    trackSearchEngagement(keyword);

    // Store the search query, timestamp, and pathname in sessionStorage
    // This helps track which pages are search-related for proper cleanup
    try {
      sessionStorage.setItem('lastSearchQuery', keyword);
      sessionStorage.setItem('lastSearchTimestamp', Date.now().toString());
      // Store just the pathname for easier comparison
      sessionStorage.setItem('lastSearchResultUrl', searchUrl.split('?')[0]);
    } catch {
      // Ignore if sessionStorage is not available
    }

    void router.push(searchUrl);

    void logSearch(keyword, router.asPath, searchUrl, 'ENTER', assetType);

    void initializeTracking({
      endSlug: searchUrl,
      content: keyword,
      contentType: 'Search',
      access: 'Search',
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const keyword = e.target.value;
    const assetType = methods.getValues('assetType');

    // Update the form value
    methods.setValue('keyword', keyword);

    // Open search immediately to make the UI feel responsive
    if (!isSearchVisible) {
      openSearch();
    }

    // Update our tracking reference with requestId 0 (will be updated by search)
    currentSearchRef.current = { keyword, assetType, requestId: 0 };

    // IMMEDIATELY clear results for empty inputs
    if (!keyword || keyword.trim() === '') {
      // Cancel any pending debounced searches
      debouncedSearch.cancel();
      // Immediately clear search results
      setSearchResults(undefined);

      // Force a refresh of component state
      void methods.trigger('keyword');

      return;
    }

    // Check the cache first - using the helper for consistency
    const cachedKey = getCacheKey(keyword, assetType);
    const cachedResult = cachedSearchResults[cachedKey];

    if (cachedResult) {
      setSearchResults(cachedResult);
    }

    // Only perform search if there's a keyword
    debouncedSearch.search(keyword, assetType);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const input = form.querySelector('input') as HTMLInputElement;
    const keyword = input.value;

    if (keyword) {
      // Blur the input to remove focus before redirect
      if (input) {
        input.blur();
      }
      submitSearch(keyword, methods.getValues('assetType'));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (
      // If the user presses Backspace or Delete in an input with 1 or 0 characters,
      // immediately reset the search
      (e.key === 'Backspace' || e.key === 'Delete') &&
      (e.currentTarget.value.length <= 1 ||
        (e.currentTarget.selectionStart === 0 &&
          e.currentTarget.selectionEnd === e.currentTarget.value.length))
    ) {
      // If we've got an entirely empty or selected search, clear results immediately
      debouncedSearch.cancel();
      setSearchResults(undefined);
    }
  };

  const handleAssetTypeChange = (value: string) => {
    // Cast to AssetType since we know the value is from a set of options
    const assetType = value as AssetType;

    // First, set the form's asset type value immediately to trigger UI updates
    methods.setValue('assetType', assetType, { shouldDirty: true });

    // Get the current search keyword before updating anything
    const keyword = methods.getValues('keyword');

    // Update our reference to the current asset type with a new request ID
    const requestId = requestIdCounter.current + 1;

    requestIdCounter.current = requestId;

    currentSearchRef.current = {
      keyword: keyword ?? '', // Use the current keyword
      assetType,
      requestId,
    };

    // For empty searches, show trending content immediately
    if (!keyword?.trim() && isSearchVisible) {
      setSearchResults(undefined);

      return;
    }

    // For searches with keywords, always trigger a new search
    if (keyword?.trim()) {
      // Check cache first for immediate feedback
      const cachedKey = getCacheKey(keyword, assetType);
      const cachedResult = cachedSearchResults[cachedKey];

      if (cachedResult) {
        // Show cached results immediately
        setSearchResults(cachedResult);
      }

      // Always trigger a new search to ensure fresh results
      debouncedSearch.immediate(keyword, assetType);
    }
  };

  const handleInputBlur = () => {
    const currentKeyword = methods.getValues('keyword');

    // Use optional chaining for better readability
    if (!currentKeyword?.trim()) {
      // Ensure search results are cleared for empty inputs
      setSearchResults(undefined);
    }
  };

  // We return the same object shape, but our internal implementation is more efficient
  return {
    handleInputChange,
    handleSubmit,
    handleKeyDown,
    handleAssetTypeChange,
    handleInputBlur,
    submitSearch,
  };
}
